import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle } from "lucide-react";

export function statusBadge(status) {
  const s = String(status || "").toUpperCase();
  if (
    s.includes("ACTIVE") ||
    s === "SUCCESS" ||
    s === "READY" ||
    s === "ASSEMBLED" ||
    s === "VERIFIED" ||
    s === "PUBLISHED" ||
    s === "AVAILABLE" ||
    s === "COVERAGE_AVAILABLE"
  ) {
    return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">{status}</Badge>;
  }
  if (s.includes("UNAVAILABLE") || s.includes("FAIL") || s === "BLOCKED") {
    return <Badge className="bg-rose-500/20 text-rose-400 border-rose-500/30">{status}</Badge>;
  }
  return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">{status || "—"}</Badge>;
}

export function SourceChip({ children }) {
  if (!children) return null;
  return (
    <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-400 bg-slate-800/80 border border-slate-700 rounded-md px-2 py-0.5">
      {children}
    </span>
  );
}

export function EmptyState({ title = "Not on this payload", detail }) {
  return (
    <div
      className="rounded-lg border border-dashed border-slate-700 bg-slate-900/40 px-3 py-4 text-sm text-slate-500 flex gap-2"
      data-testid="coverage-empty-state"
    >
      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-slate-500" />
      <div>
        <p className="text-slate-400">{title}</p>
        {detail ? <p className="text-xs text-slate-500 mt-1">{detail}</p> : null}
      </div>
    </div>
  );
}

export function FieldRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className="text-right text-slate-200 break-words">{value || "Not provided"}</span>
    </div>
  );
}

export function SectionCard({ title, icon, badge, testId, children, className = "" }) {
  return (
    <Card className={`bg-slate-950/40 border-slate-800 ${className}`} data-testid={testId}>
      <CardHeader className="pb-3">
        <CardTitle className="text-white text-base flex items-center gap-2 flex-wrap">
          {icon}
          {title}
          {badge}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">{children}</CardContent>
    </Card>
  );
}

export function CitationList({ citations }) {
  if (!citations?.length) return null;
  return (
    <div className="space-y-2" data-testid="coverage-citation-list">
      {citations.map((cit) => (
        <div key={cit.citation_id} className="border border-slate-800 rounded-lg p-3 text-sm">
          {cit.claim_text ? <p className="text-slate-200">{cit.claim_text}</p> : null}
          <p className="text-xs text-slate-500 mt-1">
            {cit.document_name || "Policy document"}
            {cit.page_number != null ? ` p.${cit.page_number}` : ""}
            {cit.section ? ` · ${cit.section}` : ""}
            {cit.grounding_status ? ` · ${cit.grounding_status}` : ""}
          </p>
          {cit.source_text ? <p className="text-xs text-slate-400 mt-2 italic">{cit.source_text}</p> : null}
          {cit.url ? (
            <a
              href={cit.url}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-emerald-400 hover:text-emerald-300 mt-2 inline-block"
            >
              Open evidence
            </a>
          ) : null}
        </div>
      ))}
    </div>
  );
}
